import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useContent } from '../../../../contexts/ContentContext';

const AUTHORS = [
  { name: '张晓明', avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20male%20designer%20portrait%2C%20clean%20studio%20background%2C%20professional%20headshot%2C%20warm%20smile%2C%20creative%20professional&width=80&height=80&seq=contrib-001&orientation=squarish' },
  { name: '林小雨', avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20female%20designer%20portrait%2C%20clean%20white%20background%2C%20professional%20headshot%2C%20friendly%20expression%2C%20creative%20style&width=80&height=80&seq=contrib-002&orientation=squarish' },
  { name: '陈思远', avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20male%20student%20portrait%2C%20casual%20style%2C%20clean%20background%2C%20confident%20expression%2C%20modern%20look&width=80&height=80&seq=contrib-003&orientation=squarish' },
  { name: '王小美', avatar: 'https://readdy.ai/api/search-image?query=young%20asian%20female%20creative%20designer%20portrait%2C%20artistic%20style%2C%20colorful%20background&width=80&height=80&seq=contrib-004&orientation=squarish' },
];

const CATEGORIES = [
  { name: 'Photoshop', color: 'bg-blue-100 text-blue-700' },
  { name: 'AI视觉生成', color: 'bg-purple-100 text-purple-700' },
  { name: 'Illustrator', color: 'bg-orange-100 text-orange-700' },
  { name: '视频剪辑', color: 'bg-pink-100 text-pink-700' },
  { name: 'UI设计', color: 'bg-teal-100 text-teal-700' },
];

export default function NoteEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNote, updateNote, getNoteById } = useContent();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    category: CATEGORIES[0].name,
    categoryColor: CATEGORIES[0].color,
    tags: [] as string[],
    author: AUTHORS[0].name,
    authorAvatar: AUTHORS[0].avatar,
    date: '刚刚',
    readTime: '5分钟',
    likes: 0,
    comments: 0,
    collects: 0,
    image: '',
    isPinned: false,
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      const note = getNoteById(Number(id));
      if (note) {
        setFormData({
          title: note.title,
          excerpt: note.excerpt,
          category: note.category,
          categoryColor: note.categoryColor,
          tags: note.tags,
          author: note.author,
          authorAvatar: note.authorAvatar,
          date: note.date,
          readTime: note.readTime,
          likes: note.likes,
          comments: note.comments,
          collects: note.collects,
          image: note.image || '',
          isPinned: note.isPinned || false,
        });
      }
    }
  }, [id, isEdit, getNoteById]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit && id) {
      updateNote(Number(id), formData);
    } else {
      addNote(formData);
    }

    navigate('/admin/notes');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const handleCategoryChange = (categoryName: string) => {
    const category = CATEGORIES.find(c => c.name === categoryName);
    if (category) {
      setFormData(prev => ({
        ...prev,
        category: category.name,
        categoryColor: category.color,
      }));
    }
  };

  const handleAuthorChange = (authorName: string) => {
    const author = AUTHORS.find(a => a.name === authorName);
    if (author) {
      setFormData(prev => ({
        ...prev,
        author: author.name,
        authorAvatar: author.avatar,
      }));
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/notes')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <i className="ri-arrow-left-line"></i>
          返回笔记列表
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{isEdit ? '编辑笔记' : '新建笔记'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              笔记标题 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="输入笔记标题"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              笔记摘要 <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              placeholder="输入笔记摘要"
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                分类 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                作者 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.author}
                onChange={(e) => handleAuthorChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer"
              >
                {AUTHORS.map(author => (
                  <option key={author.name} value={author.name}>{author.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">封面图片 URL</label>
            <input
              type="url"
              value={formData.image}
              onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="https://example.com/image.jpg（可选）"
            />
            {formData.image && (
              <img
                src={formData.image}
                alt="封面预览"
                className="mt-3 w-full h-48 object-cover rounded-lg"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">标签</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="输入标签后按回车"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
              >
                添加
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm flex items-center gap-2"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-purple-900"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPinned}
                onChange={(e) => setFormData(prev => ({ ...prev, isPinned: e.target.checked }))}
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
              />
              <span className="text-sm font-semibold text-gray-700">置顶笔记</span>
            </label>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button
            type="button"
            onClick={() => navigate('/admin/notes')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
          >
            {isEdit ? '保存修改' : '发布笔记'}
          </button>
        </div>
      </form>
    </div>
  );
}